import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Music, Play, Pause, Flame, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (!u ? u : /^https?:\/\//i.test(u) ? u : `${BACKEND}${u}`);

/**
 * Trending Sounds row for the Discover page.
 * Shows the top library tracks sorted by use_count, each with an inline preview
 * button and a "▶ Use this sound" CTA that deeplinks to /upload?mode=photo&track=<id>.
 */
export default function TrendingSounds({ limit = 12 }) {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewId, setPreviewId] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/music/trending?limit=${limit}`)
      .then((r) => {
        if (!cancelled) setTracks(r.data?.tracks || []);
      })
      .catch(() => !cancelled && setTracks([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, [limit]);

  // 10-second preview like TikTok's sound picker. The audio element still
  // fetches the full track — we just auto-pause once currentTime crosses
  // 10s. Cheaper than adding a dedicated backend preview endpoint (which
  // would require another ffmpeg pass + more cache to manage).
  const PREVIEW_LIMIT_SEC = 10;

  const togglePreview = (t) => {
    if (previewId === t.id) {
      audioRef.current?.pause();
      setPreviewId(null);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(resolveUrl(t.url));
    audio.crossOrigin = "anonymous";
    audio.onplaying = () => setPreviewLoading(null);
    audio.ontimeupdate = () => {
      if (audio.currentTime >= PREVIEW_LIMIT_SEC) {
        audio.pause();
        audio.currentTime = 0;
        setPreviewId(null);
      }
    };
    audio.onended = () => setPreviewId(null);
    audio.onerror = () => {
      setPreviewLoading(null);
      setPreviewId(null);
      toast.error("Preview unavailable");
    };
    setPreviewLoading(t.id);
    audio.play().catch(() => {
      setPreviewLoading(null);
      setPreviewId(null);
      toast.error("Preview blocked by browser");
    });
    audioRef.current = audio;
    setPreviewId(t.id);
  };

  if (loading) {
    return (
      <div className="mb-12 flex items-center gap-2 text-white/40">
        <Loader2 className="h-4 w-4 animate-spin text-[#FF007F]" />
        <p className="font-mono text-[10px] uppercase tracking-widest">
          loading sounds…
        </p>
      </div>
    );
  }
  if (!tracks.length) return null;

  return (
    <section className="mb-12" data-testid="trending-sounds-section">
      <div className="flex items-center gap-2 mb-4">
        <Flame className="h-4 w-4 text-[#FF007F]" />
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40">
          trending sounds
        </p>
        <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-white/25">
          // most.used
        </span>
      </div>
      <div
        className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 snap-x snap-mandatory"
        data-testid="trending-sounds-row"
      >
        {tracks.map((t, idx) => {
          const isPlaying = previewId === t.id;
          const uses = t.use_count || 0;
          return (
            <div
              key={t.id}
              data-testid={`trending-sound-${t.id}`}
              className="snap-start flex-shrink-0 w-56 bg-[#0A0A0C] border border-white/10 hover:border-[#FF007F]/50 transition-all group"
            >
              {/* Cover / preview button */}
              <button
                type="button"
                data-testid={`trending-sound-preview-${t.id}`}
                onClick={() => togglePreview(t)}
                className="relative w-full aspect-square bg-gradient-to-br from-[#FF007F]/25 via-black to-[#00FFFF]/20 flex items-center justify-center overflow-hidden"
                title={isPlaying ? "Pause preview" : "Preview"}
              >
                <div
                  className="absolute inset-0 opacity-40 mix-blend-screen"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 30% 30%, rgba(255,0,127,0.35), transparent 60%), radial-gradient(circle at 70% 70%, rgba(0,255,255,0.35), transparent 60%)",
                  }}
                />
                <div className="absolute top-2 left-2 font-mono text-[10px] uppercase tracking-widest text-white/70">
                  #{String(idx + 1).padStart(2, "0")}
                </div>
                {t.source === "original" && (
                  <div
                    data-testid={`trending-sound-original-${t.id}`}
                    className="absolute top-2 left-10 px-1.5 py-0.5 border border-[#FF007F] text-[#FF007F] bg-black/60 font-mono text-[9px] uppercase tracking-widest"
                  >
                    original
                  </div>
                )}
                <div className="absolute top-2 right-2 flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-white/70">
                  <Flame className="h-3 w-3 text-[#FF007F]" strokeWidth={2.5} />
                  {uses}
                </div>
                <div
                  className={`relative z-10 h-14 w-14 border-2 flex items-center justify-center transition-all ${
                    isPlaying
                      ? "border-[#FF007F] bg-[#FF007F]/20 text-[#FF007F]"
                      : "border-white/70 text-white group-hover:border-[#FF007F] group-hover:text-[#FF007F]"
                  }`}
                >
                  {previewLoading === t.id ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : isPlaying ? (
                    <Pause className="h-6 w-6" fill="currentColor" />
                  ) : (
                    <Play className="h-6 w-6" fill="currentColor" />
                  )}
                </div>
                {isPlaying && (
                  <span
                    data-testid={`trending-sound-preview-badge-${t.id}`}
                    className="absolute bottom-2 left-2 px-1.5 py-0.5 bg-[#FF007F] text-black font-mono text-[9px] uppercase tracking-widest font-bold"
                  >
                    0:10 preview
                  </span>
                )}
                <Music
                  className="absolute bottom-2 right-2 h-4 w-4 text-white/40"
                  strokeWidth={1.5}
                />
              </button>

              {/* Meta + CTA */}
              <div className="p-3">
                <Link
                  to={`/sound/${encodeURIComponent(t.id)}`}
                  data-testid={`trending-sound-open-${t.id}`}
                  className="block hover:text-[#00FFFF] transition-colors"
                >
                  <p className="text-white text-sm font-semibold truncate hover:text-[#00FFFF]">
                    {t.title}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 truncate">
                    {t.artist} · {t.genre}
                  </p>
                </Link>
                <Link
                  to={`/upload?mode=photo&track=${encodeURIComponent(t.id)}`}
                  data-testid={`trending-sound-use-${t.id}`}
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 border border-[#FF007F]/60 hover:bg-[#FF007F]/10 text-[#FF007F] font-mono text-[10px] uppercase tracking-widest py-1.5 transition-all"
                >
                  <Play className="h-3 w-3" fill="currentColor" />
                  use this sound
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
